import { useMemo, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { GrossPrice } from '@/components/billing/PricingBreakdown';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  getEligibleUpgradePlans,
  type UpgradeActionPlan,
  type UpgradeBillingCycle,
} from '@/lib/billing/upgradeEligibility';
import {
  planPricingForCycle,
  type PricingDisplayCatalog,
} from '@/lib/billing/pricingDisplay';

interface ActiveSubscriptionUpgradeActionProps {
  activePaid: boolean;
  currentPlan: UpgradeActionPlan | null | undefined;
  plans: readonly UpgradeActionPlan[];
  billingCycle: UpgradeBillingCycle;
  planChangeInProgress: boolean;
  pricingCatalog?: PricingDisplayCatalog | null;
  pricingLoading?: boolean;
  checkoutBlockedReason?: string | null;
  onSelectPlan: (plan: UpgradeActionPlan) => void;
}

export function ActiveSubscriptionUpgradeAction({
  activePaid,
  currentPlan,
  plans,
  billingCycle,
  planChangeInProgress,
  pricingCatalog,
  pricingLoading = false,
  checkoutBlockedReason,
  onSelectPlan,
}: ActiveSubscriptionUpgradeActionProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const eligiblePlans = useMemo(
    () => getEligibleUpgradePlans(plans, currentPlan, billingCycle),
    [billingCycle, currentPlan, plans],
  );

  if (!activePaid || planChangeInProgress || eligiblePlans.length === 0) return null;

  const checkoutBlocked = Boolean(checkoutBlockedReason);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 rounded-full px-3 text-xs"
        disabled={checkoutBlocked}
        title={checkoutBlockedReason ?? undefined}
        onClick={() => setOpen(true)}
      >
        <ArrowUpRight className="me-1 h-3 w-3 rtl-flip" />
        {t('hire.billingSettings.upgradePlan')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('hire.billingSettings.upgradePlansTitle')}</DialogTitle>
            <DialogDescription>
              {t('hire.billingSettings.upgradePlansDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {eligiblePlans.map((candidate) => {
              const pricing = planPricingForCycle(pricingCatalog, candidate.id, billingCycle);
              const pricingUnavailable = !pricingLoading && !pricing;
              return (
                <div
                  key={candidate.id}
                  className="flex min-w-0 flex-col gap-3 rounded-xl border border-border bg-background p-4"
                >
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-foreground">{candidate.plan_name}</p>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {pricing ? (
                        <GrossPrice
                          pricing={pricing}
                          size="small"
                          suffix={(
                            <span>
                              /{billingCycle === 'yearly'
                                ? t('hire.checkout.yearAbbr')
                                : t('hire.billingSettings.monthShort')}
                            </span>
                          )}
                        />
                      ) : (
                        <p>
                          {pricingLoading
                            ? t('pricingDisplay.loading')
                            : t('pricingDisplay.unavailable')}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    type="button"
                    className="mt-auto w-full"
                    disabled={pricingLoading || pricingUnavailable}
                    onClick={() => {
                      setOpen(false);
                      onSelectPlan(candidate);
                    }}
                  >
                    {t('hire.billingSettings.selectUpgradePlan', {
                      plan: candidate.plan_name,
                    })}
                  </Button>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
